<!-- app/components/inpostpay/InpostPayButton.vue -->
<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useInpostPay } from '~~/app/composables/useInpostPay'
import { useInpostPayOrderConfirmation } from '~~/app/composables/useInpostPayOrderConfirmation'
import { useAddToCartMutation, useCartQuery, useRefreshCart } from '~~/app/composables/queries/useStoreApi'
import { useNotifications } from '~~/app/composables/useNotifications'
import { useCartStore } from '~~/stores/cartStore'

interface Props {
  context: 'product' | 'cart' | 'checkout'
  productId?: string
  productAttributeId?: number
}

const props = withDefaults(defineProps<Props>(), {
  productId: undefined,
  productAttributeId: 0,
})

const { isReady, init, prepareForCart, setProductAddHandler, clearProductAddHandler, rerenderWidget, listenForBasketChanged } = useInpostPay()
const addToCart = useAddToCartMutation()
const refreshCart = useRefreshCart()
const notify = useNotifications()
const { data: cartData } = useCartQuery()
const cartStore = useCartStore()

useInpostPayOrderConfirmation()

// Odsubskrybowanie live-sync koszyka (ustawiane w onMounted, czyszczone w onBeforeUnmount).
let stopBasketListener: (() => void) | null = null

// Animacja wejścia przycisku. Wrapper (nasz <div>) jest celem; sam <inpost-izi-button> to
// zewnętrzny web component InPost, którego wyglądu nie animujemy. Tween trzymamy, by zabić
// go przy odmontowaniu (np. nawigacja w trakcie async-init SDK).
const wrapperEl = ref<HTMLElement | null>(null)
let _revealTween: { kill: () => void } | null = null
let _hasRevealed = false

function _prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

// Reveal w stylu panelu PDP (_animatePdpIntro w pages/products/[slug].vue: fade + slide-up,
// power2.out, 0.72s, y:24). Widget pojawia się dopiero po async-init SDK (v-if="isReady"),
// więc NIE łapie się na intro GSAP strony produktu — animujemy go tutaj, gdy wrapper wejdzie
// do DOM. Działa w obu kontekstach (desktop CTA + mobilny sticky bar) bez zmian w PDP.
async function revealWrapper(): Promise<void> {
  if (_hasRevealed) return
  const el = wrapperEl.value
  if (!el) return
  _hasRevealed = true

  // Reduced-motion: zostaw przycisk widoczny bez animacji (jak panel PDP).
  if (_prefersReducedMotion()) return

  // Ukryj natychmiast (inline), zanim doczyta się GSAP — bez tego mignąłby w pełni widoczny
  // w czasie async import("gsap"). NIE używamy CSS pre-hide (.pdp--intro-animations): gdyby
  // GSAP się nie załadował, przycisk MUSI pozostać widoczny — patrz catch poniżej.
  el.style.opacity = '0'

  try {
    const { gsap } = await import('gsap')
    // Komponent mógł zostać odmontowany w trakcie async import("gsap") (nawigacja w oknie
    // init SDK). Nie animuj odłączonego węzła — onBeforeUnmount.kill() już przeszedł.
    if (!el.isConnected) return
    _revealTween = gsap.fromTo(
      el,
      { opacity: 0, y: 24 },
      {
        opacity: 1,
        y: 0,
        duration: 0.72,
        ease: 'power2.out',
        // clearProps usuwa też transform — to load-bearing, nie kosmetyka: rezydualny
        // transform:translateY(0) uczyniłby wrapper containing-blockiem dla position:fixed
        // i mógłby przesunąć overlay InPost. Nie skracaj do samego "opacity".
        clearProps: 'opacity,transform',
      },
    )
  }
  catch {
    // GSAP się nie załadował — przywróć widoczność, nigdy nie zostawiaj ukrytego przycisku.
    el.style.opacity = ''
  }
}

// Wariant i zachowanie przycisku wynikają z binding_place (kontrakt InPost Pay Widget 2.0,
// patrz WidgetConfiguration::getIterator w module inpostizi):
//   PRODUCT_CARD   → karta produktu ("Dodaj do koszyka")
//   BASKET_SUMMARY → koszyk ("Kup z InPost")
//   CHECKOUT_PAGE  → strona kasy (express "Kup z InPost" przy istniejącym koszyku)
const bindingPlace = computed(() => {
  switch (props.context) {
    case 'product': return 'PRODUCT_CARD'
    case 'checkout': return 'CHECKOUT_PAGE'
    default: return 'BASKET_SUMMARY'
  }
})

// Skin przycisku InPost dobrany pod natywny przycisk storefrontu (56px, pigułka, font 14px):
//   size-xl → font-size:14px (= natywne 0.875rem); round → border-radius:256px (pigułka).
// Szerokość 100% zapewnia wrapper .inpostpay-button. UWAGA: kolor i logo pozostają brandowe
// InPost (żółć + logo) — tego NIE da się zmienić po stronie frontu, tylko przez panel merchanta
// (getCustomStyle). Dostępne tokeny: size-xs…size-xl, round/rounded — zmień tutaj, by dobrać skin.
const widgetVariation = 'size-xl round'

onMounted(async () => {
  // Widget mógł być gotowy już w chwili mountu (singleton zainicjalizowany przy wcześniejszym
  // mount — np. powrót na PDP). Wtedy wrapper jest w DOM od razu (v-if="isReady"), więc
  // animujemy bez czekania. Przypadek „isReady przełącza się dopiero po init SDK" łapie watcher
  // poniżej. onMounted to właściwy moment: template ref przypisany, element podłączony do DOM
  // (immediate watcher odpalałby się jeszcze w setup, zanim ref/DOM są gotowe).
  if (isReady.value) void revealWrapper()

  await init()

  if (props.context === 'product' && props.productId) {
    // Na karcie produktu widget jest niezwiązany i przy kliknięciu sam woła
    // unboundWidgetClicked → ten handler dodaje aktualnie wybrany wariant do koszyka.
    // (Zastępuje dawny DOM-intercept + syntetyczny klik, który widget ignorował.)
    setProductAddHandler(async () => {
      // Czy koszyk był pusty PRZED dodaniem? Sprawdzamy na starcie handlera, bo po
      // mutacji koszyk ma już 1 pozycję. Przy pustym koszyku binding key dla świeżo
      // utworzonego koszyka bywa niegotowy (race: event z widget-api InPost vs. nasz
      // addToCart), więc overlay InPost się nie otwiera i użytkownik nie dostaje
      // żadnego feedbacku. Otwieramy wtedy drawer koszyka — jak natywny "Dodaj do
      // koszyka" (useAddToCartFlow). Przy niepustym koszyku zostawiamy express-flow
      // InPost bez zmian (nie zaśmiecamy go drawerem).
      const wasEmpty = !cartData.value || (cartData.value.lines?.length ?? 0) === 0
      try {
        await addToCart.mutateAsync({
          productId: Number(props.productId),
          productAttributeId: props.productAttributeId ?? 0,
          quantity: 1,
        })
        if (wasEmpty) {
          cartStore.openDrawer()
        }
      }
      catch (err) {
        notify.error('Nie udało się dodać produktu do koszyka')
        throw err
      }
    })

    // Świeży mount na karcie produktu (np. powrót na PDP z karuzeli „inni oglądali
    // również", gdzie blok produktu chowa się pod v-if="product" i remontuje się)
    // NIE odpala watchera na productId (initial prop). init() jest wtedy zcache'owanym
    // no-opem (singleton widgetu już zainicjalizowany), więc nic nie skanuje nowego
    // <inpost-izi-button> i zostaje pusty. Po flush DOM wymuszamy re-render widgetu,
    // który re-skanuje DOM i hydratuje aktualny przycisk.
    await nextTick()
    rerenderWidget()
  }
  else if (props.context === 'cart' || props.context === 'checkout') {
    // W koszyku i na kasie produkty już są — wiążemy widget z istniejącym koszykiem
    // (binding key). Logika identyczna; różni je tylko binding_place przycisku.
    await prepareForCart()
  }

  // Live-sync: zmiana ilości / usunięcie produktu w aplikacji InPost emituje event
  // basketowy. Backend już zaktualizował \Cart, więc odświeżamy query koszyka, by
  // zmiany pojawiły się we froncie (koszyk, badge w headerze) bez reloadu strony.
  stopBasketListener = listenForBasketChanged(() => {
    void refreshCart()
  })
})

// Przy przełączeniu na inny produkt (nawigacja PDP→PDP albo wybór innego wariantu
// allegrohash) komponent NIE jest re-montowany — zmienia się tylko prop productId.
// Widget nie obserwuje wstawiania elementów ani zmian atrybutów, więc nowy/zmieniony
// <inpost-izi-button> zostaje pusty. Po flush DOM (nextTick) wymuszamy re-render widgetu,
// który re-skanuje przyciski i hydratuje aktualny. Handler addToCart czyta props.productId
// dopiero w momencie kliknięcia, więc nie wymaga ponownej rejestracji.
watch(
  () => props.productId,
  async (next) => {
    if (props.context !== 'product' || !next || !isReady.value) return
    await nextTick()
    rerenderWidget()
  },
)

// Normalny przypadek produkcyjny: SDK kończy async-init już PO mount → isReady przełącza się
// false→true i wrapper (v-if="isReady") dopiero teraz wchodzi do DOM. nextTick → wrapper jest
// wstawiony i ref przypisany, zanim wołamy reveal. (Przypadek „gotowy już w mount" obsługuje
// onMounted — nie używamy immediate, bo odpalałby się w setup przed przypisaniem refa.)
watch(isReady, async (ready) => {
  if (!ready) return
  await nextTick()
  void revealWrapper()
})

onBeforeUnmount(() => {
  if (props.context === 'product') clearProductAddHandler()
  stopBasketListener?.()
  _revealTween?.kill()
})
</script>

<template>
  <div v-if="isReady" ref="wrapperEl" class="inpostpay-button">
    <inpost-izi-button
      v-if="context === 'product' && productId"
      :key="productId"
      :binding_place="bindingPlace"
      :data-product-id="productId"
      :variation="widgetVariation"
    />
    <inpost-izi-button
      v-else-if="context === 'cart' || context === 'checkout'"
      :binding_place="bindingPlace"
      :variation="widgetVariation"
    />
  </div>
</template>

<style scoped>
.inpostpay-button {
  display: inline-block;
  width: 100%;
}
</style>
